import React, { Component } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import PropTypes from 'prop-types';
import { DataTable, DatePicker } from '@3plearning/react-components';
import cloneDeep from 'lodash/cloneDeep';

import HeaderToggle from '../../../components/participation-table/header-toggle/header-toggle';
import allClassesActions, {
    PARTICIPATION_ALL_CLASSES_RETRIEVED
} from './all-classes-actions';
import {
    SELECTED_DATE,
    SELECTED_CLASS,
    SELECTED_YEAR_GROUP,
    SELECTED_STUDENT,
    SELECTED_TEACHER
} from '../../../components/report-filters/filter-constants';
import { activeFiltersPropType } from '../../../components/report-filters/filter-prop-types';
import css from './all-classes.scss';
import {
    CLASS_ID,
    CLASS_NAME,
    getAllClassesParticipationTableConfig,
    NEW_CERTIFICATES_TOTAL,
    PAST_CERTIFICATES_TOTAL,
    getAllYearsParticipationTableConfig,
    getTop10ParticipationTableConfig,
    ALL_CLASSES_USAGE_TABLE_CONFIG
} from './all-classes-constants';
import activeFiltersActions from '../../../components/report-filters/redux/active-filters/active-filters-actions';
import allClassesPropTypes from './all-classes-prop-types';
import RouteHelper from '../../../helpers/route-helper';
import TableHeader from '../../../components/table-header/table-header';
import activeFiltersSelectors from '../../../components/report-filters/redux/active-filters/active-filters-selectors';
import {
    REPORT_STATE_ALL_YEARS,
    REPORT_STATE_ALL_CLASSES,
    REPORT_STATE_TOP_TEN
} from './report-state/report-state-actions';
import FilterHelper from '../../../helpers/filter-helper';
import Usage from '../../../components/usage/usage';
import { useTranslations } from '../hooks/use-translations';
import certificatesActions from '../../../components/certificates/certificates-actions';
import AllClassCertificates from './components/all-class-certificates';
/**
 * AllClasses component
 * Displays participation results table, certificates & usage for all classes
 */
export class AllClasses extends Component {
    static propTypes = {
        participationData: allClassesPropTypes.participationData,
        activeFilters: activeFiltersPropType,
        isAdmin: PropTypes.bool,
        updateActiveFilter: PropTypes.func,
        setActiveFilters: PropTypes.func,
        invalidRequest: PropTypes.string,
        reportState: PropTypes.oneOf([
            REPORT_STATE_ALL_YEARS,
            REPORT_STATE_ALL_CLASSES,
            REPORT_STATE_TOP_TEN
        ]).isRequired,
        studentClasses: PropTypes.object.isRequired,
        newCertificatesDownloadByType: PropTypes.func,
        pastCertificatesDownloadByType: PropTypes.func,
        academicYear: PropTypes.shape({
            startDate: PropTypes.string,
            endDate: PropTypes.string
        }).isRequired,
        getParticipationData: PropTypes.func,
        getTop10StudentsData: PropTypes.func,
        getByClassData: PropTypes.func
    };

    constructor(props) {
        super(props);

        this.isLandingPage = true;
        this.isAllYearsRoute = false;
        this.state = {
            participationTableConfig: null,
            dataTable: {
                isPastCertificatesExpanded: false,
                isNewCertificatesExpanded: false
            }
        };
        this.setRouteType(props, true);
        this.i18n = window.i18n;
    }

    componentDidMount() {
        const { activeFilters } = this.props;
        if (FilterHelper.shouldLoadAllClasses(activeFilters)) {
            this.getAllClassesParticipationData(this.props);
        }
    }

    componentDidUpdate(prevProps, prevState) {
        if (
            prevState.dataTable.isPastCertificatesExpanded !==
                this.state.dataTable.isPastCertificatesExpanded ||
            prevState.dataTable.isNewCertificatesExpanded !==
                this.state.dataTable.isNewCertificatesExpanded
        ) {
            this.setTableConfig(this.props);
        }
    }

    componentWillReceiveProps(newProps) {
        const {
            activeFilters,
            invalidRequest,
            updateActiveFilter,
            isAdmin,
            participationData,
            reportState
        } = this.props;

        const {
            invalidRequest: newInvalidRequest,
            activeFilters: newActiveFilters,
            isAdmin: newIsAdmin,
            participationData: newParticipationData,
            reportState: newReportState
        } = newProps;

        if (activeFilters !== newActiveFilters || isAdmin !== newIsAdmin) {
            this.setRouteType(newProps);
        }

        if (
            FilterHelper.shouldReloadAllClasses(activeFilters, newActiveFilters)
        ) {
            this.getAllClassesParticipationData(newProps);
        }

        if (
            participationData !== newParticipationData ||
            reportState !== newReportState
        ) {
            this.setTableConfig(newProps);
        }

        if (
            invalidRequest !== newInvalidRequest &&
            newInvalidRequest === PARTICIPATION_ALL_CLASSES_RETRIEVED
        ) {
            if (
                newActiveFilters[SELECTED_DATE].type !==
                DatePicker.THIS_SCHOOL_YEAR
            ) {
                updateActiveFilter(SELECTED_DATE, {
                    type: DatePicker.THIS_SCHOOL_YEAR
                });
            }
        }
    }

    getAllClassesParticipationData = (props) => {
        const {
            getParticipationData,
            getTop10StudentsData,
            getByClassData,
            reportState,
            activeFilters
        } = props;

        if (this.isAllYearsRoute && reportState === REPORT_STATE_TOP_TEN) {
            getTop10StudentsData(activeFilters[SELECTED_DATE]);
        } else if (
            this.isAllYearsRoute &&
            reportState === REPORT_STATE_ALL_CLASSES
        ) {
            getByClassData(activeFilters[SELECTED_DATE]);
        } else {
            getParticipationData(
                activeFilters[SELECTED_DATE],
                activeFilters[SELECTED_YEAR_GROUP].id,
                activeFilters[SELECTED_TEACHER].id
            );
        }
    };

    setRouteType(props, firstLoad) {
        const { isAdmin, activeFilters } = props;
        this.isLandingPage = RouteHelper.isLandingPage(isAdmin, activeFilters);
        this.isAllYearsRoute = RouteHelper.isAllYearsRoute(
            isAdmin,
            activeFilters
        );
        if (!firstLoad) {
            this.setTableConfig(props);
            this.setUsageTableConfig();
        }
    }

    getTableConfig = ({ reportState }) => {
        const isAllYearsRoute =
            this.isAllYearsRoute && reportState !== REPORT_STATE_ALL_CLASSES;
        const defaultParams = {
            isPastCertificatesExpanded: this.state.dataTable
                .isPastCertificatesExpanded,
            isNewCertificatesExpanded: this.state.dataTable
                .isNewCertificatesExpanded
        };

        let participationTableConfig;
        let filterKey = CLASS_NAME;

        if (reportState === REPORT_STATE_TOP_TEN) {
            participationTableConfig = getTop10ParticipationTableConfig(
                defaultParams
            );
        } else if (isAllYearsRoute) {
            participationTableConfig = getAllYearsParticipationTableConfig(
                defaultParams
            );
            filterKey = CLASS_ID;
        } else {
            participationTableConfig = getAllClassesParticipationTableConfig(
                defaultParams
            );
        }

        return {
            participationTableConfig: cloneDeep(participationTableConfig),
            filterKey
        };
    };

    updateTableConfig = ({ participationTableConfig, filterKey }) => {
        participationTableConfig.colGroups[0].map(
            (col) => (col.name = window.i18n._(col.name))
        );

        participationTableConfig.cols.map(
            (col) => (col.name = window.i18n._(col.name))
        );

        participationTableConfig.colGroups[0].map((item) => {
            if (item.id === PAST_CERTIFICATES_TOTAL) {
                item.headerTransform = () => (
                    <HeaderToggle
                        title={item.name}
                        onClick={(value) =>
                            this.setState({
                                dataTable: {
                                    ...this.state.dataTable,
                                    isPastCertificatesExpanded: value
                                }
                            })
                        }
                        defaultExpanded={this.state.isPastCertificatesExpanded}
                    />
                );
            }
            if (item.id === NEW_CERTIFICATES_TOTAL) {
                item.headerTransform = () => (
                    <HeaderToggle
                        title={item.name}
                        onClick={(value) =>
                            this.setState({
                                dataTable: {
                                    ...this.state.dataTable,
                                    isNewCertificatesExpanded: value
                                }
                            })
                        }
                        defaultExpanded={this.state.isNewCertificatesExpanded}
                    />
                );
            }

            return item;
        });

        participationTableConfig.cols.find(
            (col) => col.id === filterKey
        ).onClick = this.loadItemPage;

        return { participationTableConfig };
    };

    setTableConfig = (props) => {
        const config = this.getTableConfig({ reportState: props.reportState });
        const { participationTableConfig } = this.updateTableConfig(config);

        this.setState({ participationTableConfig });
    };

    setUsageTableConfig = () => {
        const allClassesUsageTableConfig = cloneDeep(
            ALL_CLASSES_USAGE_TABLE_CONFIG
        );
        allClassesUsageTableConfig.cols.map(
            (col) => (col.name = window.i18n._(col.name))
        );
        this.setState({ allClassesUsageTableConfig });
    };

    loadItemPage = (selectedItem) => {
        const {
            reportState,
            setActiveFilters,
            updateActiveFilter,
            studentClasses
        } = this.props;

        if (reportState === REPORT_STATE_TOP_TEN) {
            setActiveFilters({
                [SELECTED_STUDENT]: {
                    id: selectedItem.id,
                    name: selectedItem.classroomName
                },
                [SELECTED_CLASS]: {
                    id: studentClasses[selectedItem.id]
                }
            });
            return;
        }
        const itemType =
            this.isAllYearsRoute && reportState !== REPORT_STATE_ALL_CLASSES
                ? SELECTED_YEAR_GROUP
                : SELECTED_CLASS;
        updateActiveFilter(itemType, {
            id: selectedItem.id,
            name: selectedItem.classroomName
        });
    };

    render() {
        const { participationTableConfig } = this.state;
        const {
            participationData,
            newCertificatesDownloadByType,
            pastCertificatesDownloadByType,
            academicYear,
            activeFilters
        } = this.props;

        const usageData =
            participationData && participationData.classroomUsage[0];

        const { participation } = useTranslations();

        return (
            <div className={css.component}>
                {this.isLandingPage ? (
                    <h3>{participation}</h3>
                ) : (
                    <TableHeader title={participation} />
                )}
                <Usage {...usageData} />
                <AllClassCertificates
                    participationData={participationData}
                    newCertificatesDownloadByType={
                        newCertificatesDownloadByType
                    }
                    pastCertificatesDownloadByType={
                        pastCertificatesDownloadByType
                    }
                    activeFilters={activeFilters}
                    academicYear={academicYear}
                />
                <div className={css['participation-results-container']}>
                    <div className={css['participation-results']}>
                        <DataTable
                            config={participationTableConfig}
                            data={
                                participationData &&
                                participationData.classroomParticipations
                            }
                        />
                    </div>
                </div>
            </div>
        );
    }
}

function mapStateToProps(state) {
    return {
        academicYear: state.academicYear,
        activeFilters: activeFiltersSelectors.selectActiveFilters(state),
        participationData: state.participationAllClasses,
        isAdmin: state.isAdmin,
        invalidRequest: state.invalidRequest,
        reportState: state.reportState,
        studentClasses: state.studentClasses
    };
}

function mapDispatchToProps(dispatch) {
    return bindActionCreators(
        {
            getParticipationData: allClassesActions.getParticipationData,
            getTop10StudentsData: allClassesActions.getTop10StudentsData,
            getByClassData: allClassesActions.getByClassData,
            updateActiveFilter: activeFiltersActions.updateActiveFilter,
            setActiveFilters: activeFiltersActions.setActiveFilters,
            newCertificatesDownloadByType:
                certificatesActions.newCertificatesDownloadByType,
            pastCertificatesDownloadByType:
                certificatesActions.pastCertificatesDownloadByType
        },
        dispatch
    );
}

export default connect(mapStateToProps, mapDispatchToProps)(AllClasses);



// WEBPACK FOOTER //
// ./containers/participation/all-classes/all-classes.js